import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import services from "../../services/services";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import { useAppDispatch } from "../../store/app/store";
import { getallUsersThank } from "../../store/features/getUsersSlice";
import { useSelector } from "react-redux";
import { getTourName } from "../../services/Tour";
import { utils } from "../../utils/util";

const UserProfile = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const dispatch: any = useAppDispatch();
  const { Tours } = useSelector((state: any) => state.tours);
  const { adminPermission } = useSelector((state: any) => state.counter);

  const [profile, setProfile] = useState<any>(null);
  const [userTours, setUserTours] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddTour, setShowAddTour] = useState(false);
  const [tourSearch, setTourSearch] = useState("");
  const [selectedTour, setSelectedTour] = useState<any>(null);

  const userTourIds = userTours?.map((t: any) => t.tourId);
  const filteredTours = Tours?.data?.data
    ?.filter((tour: any) => !userTourIds?.includes(tour.productId))
    .map((tour: any) => ({
      ...tour,
      name: getTourName(tour?.name, "en") || "",
    })) || [];

  const formik = useFormik({
    initialValues: {
      firstName: "",
      email: "",
      contact: "",
      id: userId,
    },
    validate: (values: any) => {
      const errors: any = {};
      if (!values?.firstName?.trim()) errors.firstName = "Name is required";
      if (!values?.email?.trim()) errors.email = "Email is required";
      else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email.trim()))
        errors.email = "Invalid email";
      return errors;
    },
    onSubmit: async (values) => {
      const res = await services.editUser({
        ...values,
        email: values.email?.trim(),
        contact: values.contact?.trim(),
      });
      if ((res as any)?.status === 200) {
        toast.success("User updated");
        setIsEditing(false);
        loadProfile();
      } else {
        toast.error("Update failed");
      }
    },
    enableReinitialize: true,
  });


  const loadProfile = async () => {
    try {
      setLoading(true);
      const res = await services.getUserProfile(userId!);
      setProfile(res);
      formik.setValues({
        firstName: res.user.firstName || "",
        email: res.user.email || "",
        contact: res.user.contact || "",
        id: userId!,
      });
    } catch (err) {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const loadUserTours = async () => {
    try {
      const response: any = await services.userTourList(userId);
      const allTours: any = await services.getAllTour();
      const allBundles: any = await services.getActiveBundles();
      for (const item of response?.data || []) {
        if (!item.destinationName) {
          for (const tour of allTours?.data?.data || []) {
            if (item.tourId === tour.productId) {
              item.destinationName = utils.getText(tour.name);
              break;
            }
          }
          if (!item.destinationName) {
            const bundle = allBundles?.data?.data?.find((b: any) => b?.productId === item.tourId);
            if (bundle) item.destinationName = `Bundle - ${bundle.name}`;
          }
        }
      }
      setUserTours(response?.data || []);
    } catch { }
  };

  const handleDelete = async () => {
    try {
      await services.deleteUserApi(userId);
      toast.success("User deleted");
      dispatch(getallUsersThank({ page: 1, pageSize: 15 }));
      navigate("/users");
    } catch {
      toast.error("Error deleting user");
    }
  };

  useEffect(() => {
    if (userId) {
      loadProfile();
      loadUserTours();
    }
  }, [userId]);

  if (loading) {
    return (
      <div className="flex w-full h-[80vh] justify-center items-center">
        <h1 className="text-aqua text-2xl">Loading profile...</h1>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex w-full h-[80vh] justify-center items-center">
        <h1 className="text-red-500 text-2xl">User not found</h1>
      </div>
    );
  }

  const { user, redemptions, stats } = profile;

  return (
    <div className="px-8 py-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-aqua hover:underline font-semibold"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Users
        </button>
        <div className="flex gap-3">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="px-4 py-2 border border-aqua text-aqua rounded-lg hover:bg-aqua hover:text-white transition"
          >
            {isEditing ? "Cancel Edit" : "Edit"}
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Top section: User Info + Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* User Info Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">User Info</h2>
          {isEditing ? (
            <form onSubmit={formik.handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-gray-500">Name</label>
                <input
                  name="firstName"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                />
                {formik.errors.firstName && <p className="text-red-500 text-xs mt-1">{formik.errors.firstName as string}</p>}
              </div>
              <div>
                <label className="text-sm text-gray-500">Email</label>
                <input
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                />
                {formik.errors.email && <p className="text-red-500 text-xs mt-1">{formik.errors.email as string}</p>}
              </div>
              <div>
                <label className="text-sm text-gray-500">Phone</label>
                <input
                  name="contact"
                  value={formik.values.contact}
                  onChange={formik.handleChange}
                  className="w-full border rounded-lg px-3 py-2 mt-1"
                />
              </div>
              <button type="submit" className="px-6 py-2 bg-aqua text-white rounded-lg">
                Save
              </button>
            </form>
          ) : (
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Name</span>
                <span className="font-medium">{user.firstName} {user.lastName || ""}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">App Email</span>
                <span className="font-medium">{user.email || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Phone</span>
                <span className="font-medium">{user.contact || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Joined</span>
                <span className="font-medium">
                  {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "—"}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Stats Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">Stats</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Total Redemptions</span>
              <span className="font-bold text-aqua text-xl">{stats.totalRedemptions}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Cities</span>
              <div className="flex flex-wrap gap-1 justify-end">
                {stats.cities.length > 0 ? stats.cities.map((city: string) => (
                  <span key={city} className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">{city}</span>
                )) : <span className="text-gray-400">—</span>}
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Platforms</span>
              <div className="flex flex-wrap gap-1 justify-end">
                {stats.platforms.length > 0 ? stats.platforms.map((p: string) => (
                  <span key={p} className={`text-xs px-2 py-1 rounded-full ${
                    p === 'GetYourGuide' ? 'bg-orange-100 text-orange-700' :
                    p === 'Viator' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>{p}</span>
                )) : <span className="text-gray-400">—</span>}
              </div>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Languages</span>
              <div className="flex flex-wrap gap-1 justify-end">
                {stats.languages.length > 0 ? stats.languages.map((l: string) => (
                  <span key={l} className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full">{l}</span>
                )) : <span className="text-gray-400">—</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Language Comparison */}
      {redemptions.length > 0 && user.email && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-3">Language Comparison</h2>
          {(() => {
            const bookingLangs = Array.from(new Set(redemptions.map((r: any) => r.bookingLanguage).filter(Boolean))) as string[];
            return (
              <div className="flex items-center gap-4">
                <div>
                  <span className="text-gray-500 text-sm">Booking Language(s): </span>
                  {bookingLangs.map((l: string) => (
                    <span key={l} className="bg-purple-100 text-purple-700 text-sm px-2 py-1 rounded-full mr-1 capitalize">{l}</span>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Redemption History */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-800 mb-4">Redemption History</h2>
        {redemptions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-gray-500 uppercase border-b">
                <tr>
                  <th className="py-3 pr-4">Tour</th>
                  <th className="py-3 pr-4">City</th>
                  <th className="py-3 pr-4">Platform</th>
                  <th className="py-3 pr-4">Ref ID</th>
                  <th className="py-3 pr-4">Booking Email</th>
                  <th className="py-3 pr-4">Language</th>
                  <th className="py-3 pr-4">Participants</th>
                  <th className="py-3 pr-4">Redeemed</th>
                </tr>
              </thead>
              <tbody>
                {redemptions.map((r: any, idx: number) => (
                  <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="py-3 pr-4 font-medium">{r.tourName}</td>
                    <td className="py-3 pr-4">
                      {r.cityName && <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">{r.cityName}</span>}
                    </td>
                    <td className="py-3 pr-4">
                      {r.platform && (
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          r.platform === 'GetYourGuide' ? 'bg-orange-100 text-orange-700' :
                          r.platform === 'Viator' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>{r.platform}</span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-gray-500 font-mono text-xs">
                      {r.externalBookingRef || r.bookingId || "—"}
                    </td>
                    <td className="py-3 pr-4 text-gray-500 text-xs">{r.bookingEmail || "—"}</td>
                    <td className="py-3 pr-4 capitalize">{r.bookingLanguage || "—"}</td>
                    <td className="py-3 pr-4 text-center">{r.totalParticipants || "—"}</td>
                    <td className="py-3 pr-4 text-gray-500">
                      {r.redeemedAt ? new Date(r.redeemedAt).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-400">No redemptions yet</p>
        )}
      </div>

      {/* Purchased Tours */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800">Purchased Tours</h2>
          <button
            onClick={() => setShowAddTour(true)}
            className="px-4 py-2 bg-aqua text-white rounded-lg hover:opacity-90 transition"
          >
            + Add Tour
          </button>
        </div>
        {userTours.length > 0 ? (
          <ol className="list-decimal pl-6 space-y-1">
            {userTours.map((tour: any, idx: number) => (
              <li key={idx} className="text-gray-700">{tour.destinationName || tour.tourId}</li>
            ))}
          </ol>
        ) : (
          <p className="text-gray-400">No purchased tours</p>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg">
            <p className="text-lg font-semibold">Are you sure you want to delete this user?</p>
            <div className="mt-6 flex gap-4">
              <button onClick={handleDelete} className="px-6 py-3 bg-red-500 text-white rounded-lg">
                Yes, Delete
              </button>
              <button onClick={() => setShowDeleteConfirm(false)} className="px-6 py-3 border-2 rounded-lg">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Tour Modal — Searchable */}
      {showAddTour && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-8 rounded-lg shadow-lg w-[500px] max-h-[80vh] flex flex-col">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Add Tour</h3>
            <input
              type="text"
              placeholder="Search tours..."
              value={tourSearch}
              onChange={(e) => setTourSearch(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 mb-3 outline-none focus:border-aqua text-sm"
              autoFocus
            />
            <div className="overflow-y-auto flex-1 max-h-[400px] border rounded-lg mb-4">
              {filteredTours
                .filter((t: any) => t.name?.toLowerCase().includes(tourSearch.toLowerCase()))
                .map((tour: any) => (
                  <div
                    key={tour.productId}
                    onClick={() => setSelectedTour(tour)}
                    className={`px-4 py-3 cursor-pointer border-b border-gray-50 text-sm transition ${
                      selectedTour?.productId === tour.productId
                        ? "bg-aqua bg-opacity-10 text-aqua font-semibold"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    {tour.name || tour.title || tour.productId}
                  </div>
                ))}
              {filteredTours.filter((t: any) => t.name?.toLowerCase().includes(tourSearch.toLowerCase())).length === 0 && (
                <div className="px-4 py-6 text-center text-gray-400 text-sm">No tours found</div>
              )}
            </div>
            {selectedTour && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                Selected: <strong>{selectedTour.name}</strong>
              </div>
            )}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => { setShowAddTour(false); setTourSearch(""); setSelectedTour(null); }}
                className="px-6 py-2 border border-aqua text-aqua rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!selectedTour}
                onClick={async () => {
                  if (!selectedTour) return;
                  const payload = {
                    ...formik.values,
                    phone: formik.values.contact,
                    cartDetails: [{
                      productId: selectedTour.productId,
                      tourId: selectedTour.id,
                      destinationName: selectedTour.name,
                      imagesUrl: selectedTour.imageUrl,
                      placeId: selectedTour.placeId,
                      status: 1,
                      amount: selectedTour.sellingPrice,
                      bundelsId: 1,
                    }],
                  };
                  try {
                    const res = await services.createUserApi(payload);
                    if (res.status === true) {
                      toast.success("Tour added");
                      setShowAddTour(false);
                      setTourSearch("");
                      setSelectedTour(null);
                      loadUserTours();
                    }
                  } catch {
                    toast.error("Error adding tour");
                  }
                }}
                className={`px-6 py-2 rounded-lg text-white ${selectedTour ? "bg-aqua hover:opacity-90" : "bg-gray-300 cursor-not-allowed"}`}
              >
                Add Tour
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
